import { Moon, Sun } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTheme } from '@/lib/theme';
import { cn } from '@/lib/utils';

export function ThemeToggle({ className }: { className?: string }) {
  const { theme, toggleTheme } = useTheme();
  const transitionTimer = useRef<number | null>(null);
  const nextTheme = theme === 'dark' ? 'light' : 'dark';

  useEffect(
    () => () => {
      if (transitionTimer.current !== null) window.clearTimeout(transitionTimer.current);
      document.documentElement.classList.remove('theme-transitioning');
    },
    [],
  );

  const handleToggle = () => {
    document.documentElement.classList.add('theme-transitioning');
    toggleTheme();

    if (transitionTimer.current !== null) window.clearTimeout(transitionTimer.current);
    transitionTimer.current = window.setTimeout(() => {
      document.documentElement.classList.remove('theme-transitioning');
      transitionTimer.current = null;
    }, 240);
  };

  return (
    <button
      aria-label={`Switch to ${nextTheme} theme`}
      className={cn('qf-theme-toggle', className)}
      data-testid="button-theme-toggle"
      onClick={handleToggle}
      title={`Switch to ${nextTheme} theme`}
      type="button"
    >
      <Sun
        aria-hidden="true"
        className={cn(
          'absolute h-[1.05rem] w-[1.05rem] transition duration-200',
          theme === 'dark' ? 'rotate-0 scale-100 opacity-100' : 'rotate-90 scale-75 opacity-0',
        )}
      />
      <Moon
        aria-hidden="true"
        className={cn(
          'absolute h-[1.05rem] w-[1.05rem] transition duration-200',
          theme === 'light' ? 'rotate-0 scale-100 opacity-100' : '-rotate-90 scale-75 opacity-0',
        )}
      />
    </button>
  );
}
